// src/mobile_pages/const/PhotoBoard_view.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ChevronLeft,
    ChevronDown,
    Save,
    Trash2,
    Edit2,
    Image,
    FileText,
    Calendar,
    User
} from "lucide-react";
import ExportPDF from "../../components/const/ExportPDF";
import MobileShareButton from "../../components/common/mobile/MobileShareButton";
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    TouchSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import {
    getPhotoBoardDetail,
    updatePhotoBoard,
    deletePhotoBoard,
} from "../../api/const";
import PhotoCell from "../../components/const/Photo/PhotoCell";

export default function PhotoBoardEditPage() {
    const { photoboardId } = useParams();
    const navigate = useNavigate();

    const [board, setBoard] = useState(null);
    const [gridX, setGridX] = useState(2);
    const [gridY, setGridY] = useState(2);
    const [cells, setCells] = useState([]);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const [expandedSections, setExpandedSections] = useState({
        info: true,
        photos: true,
    });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 150, tolerance: 5 },
        })
    );

    // 가로/세로 비율
    const isLandscape = gridY <= gridX;
    const pdfOrientation = isLandscape ? "landscape" : "portrait";

    // 초기 데이터 불러오기
    useEffect(() => {
        getPhotoBoardDetail(photoboardId).then((data) => {
            setBoard(data);
            const x = data?.grid_x || 2;
            const y = data?.grid_y || 2;
            setGridX(x);
            setGridY(y);

            const total = x * y;
            const initCells = Array.from({ length: total }, (_, i) => ({
                id: `셀-${i + 1}`,
                photo: data.photos?.[i] || null,
            }));
            setCells(initCells);
        });
    }, [photoboardId]);

    const toggleSection = (key) => {
        setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // Drag & Drop
    const handleDragEnd = (event) => {
        if (!editing) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = cells.findIndex((c) => c.id === active.id);
        const newIndex = cells.findIndex((c) => c.id === over.id);
        setCells(arrayMove(cells, oldIndex, newIndex));
    };

    // 셀 수정/삭제
    const handleSelectPhoto = (cellId, photo) => {
        if (!editing) return;
        setCells((prev) =>
            prev.map((c) => (c.id === cellId ? { ...c, photo } : c))
        );
    };

    const handleDeleteCell = (cellId) => {
        if (!editing) return;
        setCells((prev) =>
            prev.map((c) => (c.id === cellId ? { ...c, photo: null } : c))
        );
    };

    // 저장
    const handleSave = async () => {
        setSaving(true);
        try {
            await updatePhotoBoard(photoboardId, {
                title: board?.title || "",
                confirmer: board?.confirmer || null,
                confirmer_id: board?.confirmer_id || null,
                date: board?.date || null,
                grid_x: gridX,
                grid_y: gridY,
                photo_ids: cells.map((c) => c.photo?.id).filter(Boolean),
            });
            alert("저장되었습니다!");
            setEditing(false);
        } catch (err) {
            console.error(err);
            alert("저장에 실패했습니다.");
        } finally {
            setSaving(false);
        }
    };

    // 삭제
    const handleDelete = async () => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return;
        try {
            await deletePhotoBoard(photoboardId);
            alert("삭제되었습니다!");
            navigate(-1);
        } catch (err) {
            console.error(err);
            alert("삭제에 실패했습니다.");
        }
    };

    // 뒤로가기
    const handleBack = () => {
        if (editing) {
            if (window.confirm("작성 중인 내용이 저장되지 않습니다. 나가시겠습니까?")) {
                navigate(-1);
            }
        } else {
            navigate(-1);
        }
    };

    if (!board) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <p className="text-slate-500">로딩 중...</p>
            </div>
        );
    }

    // 날짜 포맷
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("ko-KR");
    };

    return (
        <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
            <main className="w-full px-4 pt-6 pb-24">
                {/* 상단 헤더 */}
                <header className="mb-4">
                    <div className="flex items-center justify-between gap-3 min-w-0">
                        <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-500">시공관리 · 사진대지</p>
                            <h1 className="text-xl font-bold text-slate-900 truncate">
                                {board.title || "사진대지"}
                            </h1>
                        </div>

                        {/* PDF 다운로드 버튼 (열람 모드에서만) */}
                        {!editing && (
                            <div className="flex-shrink-0">
                                <ExportPDF
                                    contentId="photoboard-view-canvas"
                                    orientation={pdfOrientation}
                                />
                            </div>
                        )}
                    </div>
                </header>

                <div className="space-y-3" id="photoboard-view-canvas">
                    {/* 기본 정보 카드 */}
                    <Section
                        title="기본 정보"
                        expanded={expandedSections.info}
                        onToggle={() => toggleSection("info")}
                        icon={<FileText size={16} className="text-blue-500" />}
                    >
                        <div className="space-y-3">
                            {/* 제목 */}
                            <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                                <span className="text-sm text-slate-500 w-16 flex-shrink-0">제목</span>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={board.title || ""}
                                        onChange={(e) => setBoard({ ...board, title: e.target.value })}
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm"
                                        placeholder="제목 입력"
                                    />
                                ) : (
                                    <span className="text-sm font-medium text-slate-800">
                                        {board.title || "-"}
                                    </span>
                                )}
                            </div>

                            {/* 작성자 & 작성일 (가로 2분할) */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-lg">
                                    <User size={14} className="text-slate-400" />
                                    <div>
                                        <p className="text-[10px] text-slate-400">작성자</p>
                                        <p className="text-sm font-medium text-slate-700">
                                            {board.created_by || board.confirmer || "-"}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-lg">
                                    <Calendar size={14} className="text-slate-400" />
                                    <div>
                                        <p className="text-[10px] text-slate-400">작성일</p>
                                        <p className="text-sm font-medium text-slate-700">
                                            {formatDate(board.date || board.created_at)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 프로젝트 */}
                            <div className="flex items-center gap-3 py-2 border-t border-gray-100">
                                <span className="text-sm text-slate-500 w-16 flex-shrink-0">프로젝트</span>
                                <span className="text-sm text-slate-700">
                                    {board.project_name || "-"}
                                </span>
                            </div>
                        </div>
                    </Section>

                    {/* 사진 갤러리 섹션 */}
                    <Section
                        title={`사진 (${cells.filter(c => c.photo).length}/${cells.length})`}
                        expanded={expandedSections.photos}
                        onToggle={() => toggleSection("photos")}
                        icon={<Image size={16} className="text-green-500" />}
                    >
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={cells.map((c) => c.id)}
                                strategy={rectSortingStrategy}
                            >
                                <div
                                    className="grid gap-2"
                                    style={{
                                        gridTemplateColumns: `repeat(${gridX}, 1fr)`,
                                    }}
                                >
                                    {cells.map((cell) => (
                                        <div
                                            key={cell.id}
                                            className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
                                        >
                                            <PhotoCell
                                                id={cell.id}
                                                photo={cell.photo}
                                                projectId={board?.project}
                                                onSelect={handleSelectPhoto}
                                                onDelete={handleDeleteCell}
                                                disabled={!editing}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </Section>
                </div>
            </main>

            {/* 모바일 하단 고정 액션바 */}
            <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
                {editing ? (
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => setEditing(false)}
                            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <Save size={18} />
                            {saving ? "저장 중..." : "저장"}
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-2">
                        <button
                            type="button"
                            onClick={handleBack}
                            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
                        >
                            <ChevronLeft size={16} />
                            목록
                        </button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="w-full h-11 rounded-full border border-red-300 bg-white text-red-500 text-sm font-medium hover:bg-red-50 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
                        >
                            <Trash2 size={16} />
                            삭제
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditing(true)}
                            className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
                        >
                            <Edit2 size={16} />
                            편집
                        </button>
                    </div>
                )}
            </footer>
        </div>
    );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, icon, children }) {
    return (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
                </div>
                <ChevronDown
                    size={18}
                    className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
                        }`}
                />
            </button>
            {expanded && <div className="p-4">{children}</div>}
        </section>
    );
}
